import { ComparatorService } from '../../../shared/services/comparator-service';
import {
  GbgrundbuchmeldungService,
  GrundbuchmeldungListEntryMeta,
  GrundbuchmeldungSearchRequest,
  GrundbuchmeldungSearchResponseMeta
} from '../../../core/gb-api';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { Location, NgClass, NgTemplateOutlet } from '@angular/common';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { GbPrintService } from '../../../core/services/app-print.service';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { map, tap } from 'rxjs/operators';
import { ListLoaderObservable } from '../../../shared/components/list-loader/list-loader-observable';
import { AbstractNavigateBackComponent } from '../../../shared/components/abstract-component/abstract-navigate-back.component';
import { sanatizedSaveAs } from '../../../shared/functions/file.functions';
import { TimepointService } from 'app/core/services/timepoint.service';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { FunctionbarSearchresultButtonsComponent } from '../../../shared/components/title-functionbar/functionbar-searchresult-buttons/functionbar-searchresult-buttons.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { KompaktGrundbuchgeschaeftComponent } from '../../../shared/kompaktdarstellung/grundbuchgeschaeft/kompakt-grundbuchgeschaeft.component';
import { LinkComponent } from '../../../shared/components/link/link.component';
import { DatumPipe } from '../../../shared/pipes/simple/datum.pipe';
import { TranslateNamePipe } from '../../../shared/pipes/simple/translate-name.pipe';
import GeschaeftsstatusEnum = GrundbuchmeldungListEntryMeta.GeschaeftsstatusEnum;
import BearbeitungsstatusEnum = GrundbuchmeldungListEntryMeta.BearbeitungsstatusEnum;

@Component({
  selector: 'cap-grundbuchmeldung-list',
  templateUrl: './grundbuchmeldung-list.component.html',
  styleUrls: ['./grundbuchmeldung-list.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    FunctionbarSearchresultButtonsComponent,
    NgClass,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    CapSpinnerSmallComponent,
    ColumnHeaderComponent,
    KompaktGrundbuchgeschaeftComponent,
    LinkComponent,
    TranslatePipe,
    DatumPipe,
    TranslateNamePipe
  ]
})
export class GrundbuchmeldungListComponent extends AbstractNavigateBackComponent implements OnInit, OnDestroy {
  listLoader: ListLoader<GrundbuchmeldungListEntryMeta>;
  tableSortModel = new TableSortModel<GrundbuchmeldungListEntryMeta>('table', this.comparatorService);

  grundbuchmeldungResponse: GrundbuchmeldungSearchResponseMeta;
  grundbuchmeldungRequest: GrundbuchmeldungSearchRequest;

  showBearbeitungsstatus = false;

  isDownloading = false;

  constructor(
    private grundbuchmeldungService: GbgrundbuchmeldungService,
    private route: ActivatedRoute,
    protected translate: TranslateService,
    protected capAlertService: CapAlertService,
    protected location: Location,
    protected comparatorService: ComparatorService,
    private printService: GbPrintService,
    private timepointService: TimepointService
  ) {
    super(translate, capAlertService, location);
    keysToTranslate<string>([
      'MeldungenAlsZip',
      'GrundbuchMeldungZip',
      'GemaessGrundbuch',
      'breadcrumbauskunftgrundbuchmeldunglist',
      'Bearbeitungsstatus',
      'BetroffeneGemeinden',
      'Abschlussdatum',
      'Geschäftsstatus',
      'Tagebuchnummer',
      'detailsGeschäft',
      'grundbuchmeldung.tooltip',
      'nichtBearbeitet',
      'grundbuchmeldungen.ListTitel'
    ]);
    keysToTranslate<BearbeitungsstatusEnum | GeschaeftsstatusEnum>([
      'BEARBEITET',
      'NICHTBEARBEITET',
      'TAGEBUCH',
      'TAGEBUCH_ABGESCHLOSSEN',
      'VORPRUEFUNG',
      'HAUPTBUCH',
      'HAUPTBUCH_ABGESCHLOSSEN',
      'VERIFIKATION',
      'RECHTSGUELTIG',
      'RECHTSGUELTIG_AUSGETRAGEN',
      'SUSPENDIERT',
      'EXMATRIKULIERT',
      'PROVISORISCH_ABGEWIESEN',
      'REKURS',
      'REKURS_AKZEPTIERT',
      'ABGEWIESEN',
      'ABGEWIESEN_AUSGETRAGEN',
      'ZURUECKGEZOGEN',
      'ZURUECKGEZOGEN_AUSGETRAGEN'
    ]);
    this.tableSortModel
      .addColumn('geschaeftsNummer', (a, b) =>
        this.comparatorService.geschaeftComparator(a.grundbuchgeschaeft.timePoint, b.grundbuchgeschaeft.timePoint)
      )
      .switchSortDirection();
    this.tableSortModel.addColumn('geschaeftsstatus', (a, b) =>
      this.comparatorService.translatedStringComparator(a.geschaeftsstatus.toString(), b.geschaeftsstatus.toString())
    );
    this.tableSortModel.addColumn('abschlussdatum', (a, b) => this.comparatorService.dateComparator(a.abschlussDatum, b.abschlussDatum));
    this.tableSortModel.addColumn('bearbeitungsstatus', (a, b) =>
      this.comparatorService.translatedStringComparator(a.bearbeitungsstatus.toString(), b.bearbeitungsstatus.toString())
    );
  }

  ngOnInit() {
    this.grundbuchmeldungRequest = JSON.parse(this.route.snapshot.queryParams['request']);
    this.grundbuchmeldungRequest.geschaeftsstatusList = null;
    const z = this.timepointService.timepointCtx;
    const gbreq = this.grundbuchmeldungRequest;
    const tbn = gbreq.tagebuchnummer ? gbreq.tagebuchnummer : {};
    const gsn = gbreq.grundstueckNummer ? gbreq.grundstueckNummer : {};
    const observable = this.grundbuchmeldungService
      .searchGrundbuchmeldung(
        tbn.grundbuchamtId ? tbn.grundbuchamtId.id : null,
        tbn.jahr,
        tbn.nummer,
        tbn.index,
        gsn.gbGemeindeId ? gsn.gbGemeindeId.id : null,
        gsn.nummer,
        gsn.nummerZusatz,
        gsn.nummer3,
        gsn.nummer4,
        gbreq.egrid ? gbreq.egrid.value : null,
        gbreq.eingangsDatumVon,
        gbreq.eingangsDatumBis,
        gbreq.abschlussDatumVon,
        gbreq.abschlussDatumBis,
        gbreq.geschaeftsstatus,
        gbreq.name,
        gbreq.vorname,
        gbreq.gebaeudeversicherungsNr,
        gbreq.gwrEgid,
        gbreq.bearbeitungsstatus ? gbreq.bearbeitungsstatus.bearbeitet : null,
        gbreq.bearbeitungsstatus ? gbreq.bearbeitungsstatus.nichtBearbeitet : null,
        gbreq.registraturText,
        gsn.region ? gsn.region.id : null,
        z.timepointIso
      )
      .pipe(
        tap((response) => (this.grundbuchmeldungResponse = response)),
        tap((response) => (this.showBearbeitungsstatus = this.setShowBearbeitungsstatus(response))),
        map((response) => response.results)
      );
    this.listLoader = new ListLoaderObservable(15, 1, observable);
    this.addLoaderAndNavigateBackIfEmpty(this.listLoader);
    this.listLoader.applySortModel(this.tableSortModel);
  }

  getZip() {
    if (this.grundbuchmeldungResponse.hasPrintRight) {
      this.isDownloading = true;
      this.addSub(
        this.printService
          .downloadGrundbuchmeldungenZip(this.grundbuchmeldungRequest)
          .subscribe((param) => this.getZipFromArray(param))
          .add(() => (this.isDownloading = false))
      );
    }
  }

  private setShowBearbeitungsstatus(response: GrundbuchmeldungSearchResponseMeta): boolean {
    return response.results[0] && response.results[0].mBearbeitungsstatus.access !== 'NONE';
  }

  public getZipFromArray(data) {
    const file: Blob & { name?: string } = new Blob([data], { type: 'application/zip' });
    file.name = this.translate.instant('GrundbuchMeldungZip');
    sanatizedSaveAs(file);
  }

  ngOnDestroy() {
    this.listLoader.unsubscribeAll();
  }
}
